/**
 * @file ScheduleEmployeePicker.tsx
 * @brief Required employee picker for booking a session.
 *
 * @author DES
 * @date 19-06-2026
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList, type SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import { useEmployeesList } from "@/features/employees/api";
import type { CompanyEmployee } from "@/features/employees/schemas";

interface ScheduleEmployeePickerProps {
  value?: string | null;
  onChange: (employee: CompanyEmployee | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function ScheduleEmployeePicker({
  value,
  onChange,
  placeholder = "Select employee",
  disabled,
}: ScheduleEmployeePickerProps) {
  const [open, setOpen] = useState(false);
  const employees = useEmployeesList();

  const selected = useMemo(
    () => employees.data?.find((e) => e.id === value) ?? null,
    [employees.data, value],
  );

  const items: SearchableMenuItem[] = useMemo(() => {
    return (employees.data ?? []).map((e) => {
      const name = formatFullName(e.first_name, e.last_name);
      return {
        value: e.id,
        label: name !== "—" ? name : e.email,
        description: e.email,
        keywords: [e.first_name, e.last_name].filter(Boolean).join(" "),
        icon: (
          <Avatar
            src={e.profile_image_url}
            initials={getInitials(e.first_name, e.last_name, e.email)}
            gender={e.gender}
            alt=""
            size="sm"
          />
        ),
      };
    });
  }, [employees.data]);

  const triggerLabel = selected
    ? formatFullName(selected.first_name, selected.last_name) !== "—"
      ? formatFullName(selected.first_name, selected.last_name)
      : selected.email
    : placeholder;

  return (
    // A dropdown under the button, searchable (DES 30-09-2026: "ab user
    // picker aisa banao"), in place of the side sheet.
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled || employees.isLoading}
          className={cn(
            "inline-flex h-10 w-full items-center justify-between gap-2 rounded-full border border-input bg-field px-5 text-sm transition-colors",
            "hover:bg-muted/50 disabled:opacity-50",
            !selected && "text-muted-foreground",
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {selected ? (
              <Avatar
                src={selected.profile_image_url}
                initials={getInitials(selected.first_name, selected.last_name, selected.email)}
                gender={selected.gender}
                alt={triggerLabel}
                size="sm"
              />
            ) : (
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand">
                <UserIcon className="h-4 w-4" />
              </span>
            )}
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-72 p-1.5"
      >
        <SearchableMenuList
          title="Who is this session for?"
          searchPlaceholder="Search employees"
          items={items}
          loading={employees.isLoading}
          emptyText="No employees yet."
          selected={value ? [value] : []}
          onSelect={(id) => onChange(employees.data?.find((e) => e.id === id) ?? null)}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
